/**
 * ActionMenu — modular viewport-aware action menu for React components.
 *
 * Mirrors the outline engine's positioning strategy exactly:
 *   position: fixed, anchored by getBoundingClientRect() of the trigger button,
 *   with overflow handling at bottom and right viewport edges.
 *   Rendered via createPortal to document.body — escapes all stacking contexts.
 *
 * Usage:
 *   const menu = useActionMenu()
 *
 *   <ActionMenuButton menu={menu} />
 *   <ActionMenu menu={menu}>
 *     <ActionMenuItem onClick={...}>Do something</ActionMenuItem>
 *     <ActionMenuDivider />
 *     <ActionMenuItem onClick={...} danger>Delete</ActionMenuItem>
 *   </ActionMenu>
 *
 * ActionMenuButton is THE one trigger for all React node rows — a horizontal ⋯
 * in the ell grid column (pass `triangle` to place it in the tri column for rows
 * with no expand triangle). Never hand-roll a trigger (no SVG dots, no "+").
 *
 * The ActionMenu must be rendered as a sibling of (or outside) the trigger's
 * containing element — it uses createPortal so DOM placement doesn't affect
 * visual positioning, but keeping it outside .wf-row avoids grid-flow surprises.
 */

import { useState, useRef, useEffect, useLayoutEffect, useCallback, createContext, useContext } from 'react'
import { createPortal } from 'react-dom'
import { placeMenu } from '../lib/menuPlacement.js'
import { sweepSubtree, sweepScopeForAnchor } from '../outline/subtreeSweep.js'

const MenuCloseContext = createContext(null)
const MenuAnchorContext = createContext(null)

/* ── Hook ────────────────────────────────────────────────────────────────── */

/**
 * useActionMenu() — manages open/close state for one action menu.
 *
 * @returns {{ isOpen, anchor, open, close, toggle }}
 *   anchor: the trigger HTMLElement (null = closed)
 *   open(el)   — open menu anchored to el
 *   close()    — close menu
 *   toggle(el) — open if closed, close if already open
 */
export function useActionMenu() {
  const [anchor, setAnchor] = useState(null)
  return {
    isOpen: anchor !== null,
    anchor,
    open:   el => setAnchor(el),
    close:  ()  => setAnchor(null),
    toggle: el  => setAnchor(prev => prev ? null : el),
  }
}

/* ── Component ───────────────────────────────────────────────────────────── */

/**
 * ActionMenu — the floating menu itself.
 *
 * @param {{ anchor, close }} menu  — from useActionMenu()
 * @param {ReactNode}         children — <ActionMenuItem> / <ActionMenuDivider>
 */
export function ActionMenu({ menu, children, zIndex = 99999 }) {
  const { anchor, close } = menu
  const menuRef = useRef(null)

  // Start hidden so we can measure before showing (avoids single-frame flash)
  const [style, setStyle] = useState({
    position: 'fixed',
    visibility: 'hidden',
    zIndex,
    top: 0,
    left: 0,
  })

  const reposition = useCallback(() => {
    if (!anchor || !menuRef.current) return

    // Below, above, or capped-and-scrolling when it fits on neither side (lib/menuPlacement.js,
    // shared with the outline engine's menus — 2026-09-24). scrollHeight is the full height even
    // while a previous cap is applied, so a reposition never measures the capped size.
    const btn = anchor.getBoundingClientRect()
    const { top, left, maxHeight } = placeMenu(btn, menuRef.current.scrollHeight, menuRef.current.offsetWidth)
    setStyle({ position: 'fixed', visibility: 'visible', zIndex, top, left, ...(maxHeight ? { maxHeight, overflowY: 'auto' } : {}) })
  }, [anchor, zIndex])

  // Calculate position after the anchor or content changes — useLayoutEffect
  // runs synchronously after DOM mutations, before the browser paints.
  useLayoutEffect(() => { reposition() }, [anchor, children, reposition])

  // Track the trigger through scroll/resize. Without this the menu is anchored to the
  // VIEWPORT coordinates captured the instant it opened — fine for a page that only scrolls
  // at the document level, but a full-bleed workspace (Pulse) locks document scroll and does
  // its own scrolling in an inner .content container instead, so the trigger moves under a
  // menu that never does, visibly detaching them. `capture: true` is required: scroll does
  // not bubble, so a plain window listener only ever hears window-level scroll, never an
  // ancestor container's own scroll — capture is the one phase that reaches every scrollable
  // ancestor. This is a fix to the one canonical menu component, so it corrects the gap
  // everywhere a menu can open inside a scrollable region, not just where it was reported.
  useEffect(() => {
    if (!anchor) return
    window.addEventListener('scroll', reposition, { capture: true, passive: true })
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, { capture: true })
      window.removeEventListener('resize', reposition)
    }
  }, [anchor, reposition])

  // Close when user clicks outside the menu (mousedown fires before click,
  // so the old menu closes before a new ⋯ button's onClick fires — clean swap).
  // `anchor.contains(e.target)` rather than `e.target === anchor`: an anchor with children
  // (the Flight Tracker's on-curve icons anchor to a div wrapping an <svg>) reports the CHILD
  // as the mousedown target, which the strict equality misread as an outside click.
  useEffect(() => {
    if (!anchor) return
    function onMouseDown(e) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        !(anchor.contains && anchor.contains(e.target))
      ) {
        close()
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [anchor, close])

  // Close on Escape key
  useEffect(() => {
    if (!anchor) return
    function onKey(e) { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [anchor, close])

  if (!anchor) return null

  return createPortal(
    <MenuCloseContext.Provider value={close}>
      <MenuAnchorContext.Provider value={anchor}>
        <div ref={menuRef} className="wf-menu" style={style}>
          {children}
        </div>
      </MenuAnchorContext.Provider>
    </MenuCloseContext.Provider>,
    document.body
  )
}

/* ── Primitives ──────────────────────────────────────────────────────────── */

/**
 * ActionMenuButton — THE canonical action-menu trigger for React node rows.
 * Renders the standard horizontal ellipsis (⋯) styled by .wf-menu-btn.
 *
 * @param {{ toggle }} menu     — from useActionMenu()
 * @param {boolean}    triangle — place in the tri grid column (rows without an
 *                               expand triangle) via .wf-menu-btn--triangle
 * @param {string}     [title]  — optional tooltip
 */
export function ActionMenuButton({ menu, triangle, title }) {
  return (
    <button
      className={'wf-menu-btn' + (triangle ? ' wf-menu-btn--triangle' : '')}
      title={title}
      onClick={e => { e.stopPropagation(); menu.toggle(e.currentTarget) }}
    >&#8943;</button>
  )
}

/**
 * ActionMenuItem — a button row inside ActionMenu.
 * Uses .wf-menu-item CSS (same class as outline engine menus).
 *
 * @param {function} onClick
 * @param {boolean}  [danger] — renders label in red
 * @param {string}   [title]  — hover tooltip explaining what the action will do
 */
export function ActionMenuItem({ onClick, danger, title, children }) {
  const close = useContext(MenuCloseContext)
  const anchor = useContext(MenuAnchorContext)
  // "Expand all" / "Collapse all" on any outline node (or the zoom title) finishes with the shared
  // DOM sweep, so every node type opens/closes all the way down whatever its own handler covers
  // (outline/subtreeSweep.js — Brandon's AT-R2SUU). Scope is read BEFORE the handler re-renders.
  const sweep = children === 'Expand all' ? true : children === 'Collapse all' ? false : null
  return (
    <button
      className="wf-menu-item"
      title={title || undefined}
      onClick={e => {
        const scope = sweep !== null ? sweepScopeForAnchor(anchor) : null
        close?.(); onClick?.(e)
        if (scope) sweepSubtree(scope.key, sweep)
      }}
      style={danger ? { color: '#c0392b' } : undefined}
    >
      {children}
    </button>
  )
}

/**
 * ActionMenuDivider — a thin horizontal rule between menu sections.
 * Uses .wf-menu-divider CSS (same class as outline engine menus).
 */
export function ActionMenuDivider() {
  return <div className="wf-menu-divider" />
}
